import {
    NovuProvider,
    PopoverNotificationCenter,
    useSocket
} from '@novu/notification-center';
import { useAuth, useMixpanel } from '@tenderd/track-web-utils';
import { Badge, Menu, Modal, Popover } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga';
import { navigateToUrl } from 'single-spa';
import './app.css';
import ZohoBugModal from './components/ZohoBugModal';
import { useTranslation } from './translations/hooks';
import { SideBarTKeys } from './translations/keys';

export enum MenuEvents {
    ClickedTenderdLogo = 'Clicked Tenderd Logo (Sidebar)',
    ClickedMarketplace = 'Clicked Marketplace  (Sidebar)',
    ClickedLogistics = 'Clicked Logistics (Sidebar)',
    ClickedMyOrders = 'Clicked My Orders (Sidebar)',
    ClickedTrackProjects = 'Clicked Track Projects (Sidebar)',
    ClickedSettings = 'Clicked Settings (Top menu)',
    ClickedLoginSignUp = 'Clicked Login | Signup (Sidebar)',
    ClickedHelpAndSupport = 'Clicked Help And Support (Sidebar)',
    ClickedTendering = 'Clicked Tendering  (Sidebar)',
    ClickedMyMachines = 'Clicked My Machines  (Sidebar)',
    ClickedMyRequests = 'Clicked My Requests  (Sidebar)',
    ClickedNotification = 'Clicked Notifications (Top menu)',
}

export enum HelpAndSupportEvents {
    ClickedPhoneNumber = 'Clicked Phone Number',
    ClickedChatSupport = 'Clicked Chat Support',
    ClickedReportBug = 'Clicked Report Bug',
}

const NavLink: React.FunctionComponent<{
    to: string;
    children: any;
    style?: React.CSSProperties;
    onClick?: () => any;
}> = ({ to, children, onClick, style }) => {
    return (
        <div
            style={style}
            role="button"
            className="top-menu-action-btn"
            tabIndex={0}
            onClick={() => {
                if (onClick) onClick();
                navigateToUrl(to);
            }}
        >
            {children}
        </div>
    );
};

const Notifier = () => {
    const { socket } = useSocket();
    const audioRef = useRef<HTMLAudioElement>(null);
    useEffect(() => {
        if (socket) {
            socket.on('unseen_count_changed', (data) => {
                audioRef.current.muted = false;
                audioRef.current.volume = 0.15;
                audioRef.current.play();
            });
        }

        return () => {
            if (socket) {
                socket.off('unseen_count_changed');
            }
        };
    }, [socket]);

    return (
        <>
            <audio
                autoPlay={false}
                muted
                id="audio"
                src={
                    'https://tenderd-ui.storage.googleapis.com/assets/notification-sound.mp3'
                }
                ref={audioRef}
            />
        </>
    );
};

const TopMenus: React.FC = () => {
    const url = window.location.origin;
    const { account, user } = useAuth();
    const mixpanel = useMixpanel();
    const [developer, setDeveloper] = useState(false);
    const [overrides, setOverrides] = useState(false);
    const [toggleChat, setToggleChat] = useState(false);
    const [open, setOpen] = useState(false);
    const openRef = useRef<boolean>(null);
    const i18n = useTranslation();

    useEffect(() => {
        const salesiq = (window as any).$zoho?.salesiq;
        if (!salesiq) return;
        if (toggleChat) {
            const toggledVisibility =
                salesiq.values.floatvisible === 'show' ? 'hide' : 'show';
            salesiq.floatbutton.visible(toggledVisibility);
            salesiq.floatwindow.visible(toggledVisibility);
            setToggleChat(false);
        }
    }, [toggleChat, (window as any).$zoho?.salesiq]);

    useEffect(() => {
        let dev = window.localStorage.getItem('devtools');
        if (dev && dev === 'true') {
            setDeveloper(true);
        }
    }, []);

    const removeLocalStorage = (val: string[]) => {
        val.forEach((item) => {
            localStorage.removeItem(item);
        });
    };
    const handleOpenChange = (newOpen: boolean) => {
        openRef.current = newOpen;
        setOpen(newOpen);
    };

    return (
        <>
            <Modal
                width={800}
                bodyStyle={{ maxHeight: 600, overflowY: 'auto' }}
                closable={false}
                title="Developer overrides"
                open={overrides}
                onOk={() => window.location.reload()}
                onCancel={() => setOverrides(false)}
            >
                {overrides && (
                    // @ts-ignore
                    <import-map-overrides-list></import-map-overrides-list>
                )}
            </Modal>
            <div className="top-right-menu">
                <Menu
                    mode="horizontal"
                    disabledOverflow
                    selectable={false}
                    items={[
                        ...(developer
                            ? [
                                {
                                    key: 'developer',
                                    title: 'Developer',
                                    label: (
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            className="top-menu-action-btn"
                                            onClick={() => {
                                                setOverrides((a) => !a);
                                            }}
                                        >
                                            <img
                                                className="header-icons"
                                                src="https://assets.tenderd.com/header-icons/computer-programming.svg"
                                            />
                                        </div>
                                    )
                                }
                            ]
                            : []),
                        {
                            key: 'dashboard',
                            title: 'Notifications',
                            label: (
                                <div
                                    role="button"
                                    className="top-menu-action-btn"
                                    style={{ position: 'relative' }}
                                    tabIndex={0}
                                >
                                    <NovuProvider
                                        subscriberId={user?.id || ''}
                                        applicationIdentifier="i6t-0Is7MZ2R"
                                        stores={[
                                            {
                                                storeId: 'unseen',
                                                query: {
                                                    read: false
                                                }
                                            },
                                            {
                                                storeId: 'seen',
                                                query: { read: true }
                                            }
                                        ]}
                                    >
                                        <Notifier />
                                        <PopoverNotificationCenter
                                            position="right-start"
                                            tabs={[
                                                {
                                                    name: 'Unread',
                                                    storeId: 'unseen'
                                                },
                                                {
                                                    name: 'Read',
                                                    storeId: 'seen',
                                                }
                                            ]}
                                            theme={{
                                                light: {
                                                    loaderColor:
                                                        '#1890ff',
                                                    header: {
                                                        badgeColor:
                                                            '#1890ff'
                                                    },
                                                    notificationItem:
                                                    {
                                                        buttons: {
                                                            // @ts-ignore
                                                            primary:
                                                            {
                                                                backGroundColor:
                                                                    '#1890ff'
                                                            },
                                                            // @ts-ignore
                                                            secondary:
                                                            {
                                                                backGroundColor:
                                                                    '#1890ff'
                                                            }
                                                        },
                                                        unseen: {
                                                            notificationItemBeforeBrandColor:
                                                                '#1890ff',
                                                            timeMarkFontColor:
                                                                '#666666'
                                                        },
                                                        seen: {
                                                            timeMarkFontColor:
                                                                '#666666'
                                                        }
                                                    },
                                                    userPreferences: {
                                                        accordionItem:
                                                        {
                                                            switch: {
                                                                backgroundChecked:
                                                                    '#1890ff'
                                                            }
                                                        }
                                                    }
                                                }
                                            }}
                                            colorScheme="light"
                                            onActionClick={(
                                                template,
                                                type,
                                                message
                                            ) => {
                                                let url =
                                                    message.cta.data
                                                        ?.url;
                                                let isLocal =
                                                    window.location.href.includes(
                                                        'localhost:9005'
                                                    );
                                                if (url) {
                                                    window.location.href =
                                                        isLocal
                                                            ? url.replace(
                                                                'https://track.tenderd.com',
                                                                'http://localhost:9005'
                                                            )
                                                            : url;
                                                }
                                            }}
                                            footer={() => <></>}
                                        >
                                            {({ unseenCount }) => (
                                                <Badge
                                                    count={
                                                        unseenCount
                                                    }
                                                    size="small"
                                                    style={{
                                                        fontWeight:
                                                            '600',
                                                        fontSize: 10
                                                    }}
                                                >
                                                    <img onClick={() => {
                                                        mixpanel.track(
                                                            MenuEvents.ClickedNotification
                                                        );
                                                    }} className="header-icons" src="https://assets.tenderd.com/header-icons/bell.svg" alt="Notification icon" />
                                                </Badge>
                                            )}
                                        </PopoverNotificationCenter>
                                    </NovuProvider>
                                </div>
                            )
                        },
                        {
                            key: 'chat-support',
                            title: i18n.t(SideBarTKeys.chatSupport),
                            label: (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className="top-menu-action-btn img-wrapper"
                                    onClick={() => {
                                        setToggleChat(true);
                                        ReactGA.event({
                                            category:
                                                HelpAndSupportEvents.ClickedChatSupport,
                                            action: 'Track WebApp'
                                        });
                                        mixpanel.track(
                                            HelpAndSupportEvents.ClickedChatSupport
                                        );
                                    }}
                                >
                                    <img
                                        alt="chat support icon"
                                        className="header-icons"
                                        src="https://assets.tenderd.com/header-icons/chat.svg"
                                    />
                                </div>
                            )
                        },


                        {
                            key: 'settings',
                            title: i18n.t(SideBarTKeys.settings),
                            label: (
                                <NavLink
                                    to={`${url}/settings`}
                                    onClick={() => {
                                        ReactGA.event({
                                            category:
                                                MenuEvents.ClickedSettings,
                                            action: 'Track WebApp'
                                        });
                                        mixpanel.track(
                                            MenuEvents.ClickedSettings
                                        );
                                    }}
                                >
                                    <img
                                        alt="settings icon"
                                        className="header-icons"
                                        src="https://assets.tenderd.com/header-icons/settings.svg"
                                    />
                                </NavLink>
                            )
                        },
                        {
                            key: 'report-bug',
                            title: 'Report a bug',
                            label: (
                                <Popover
                                    open={open}
                                    onOpenChange={handleOpenChange}
                                    trigger={'click'}
                                    content={() => {
                                        return (
                                            <ZohoBugModal
                                                user={user}
                                                open={new Date().getTime()}
                                            />
                                        );
                                    }}
                                >
                                    <div
                                        onClick={() => {
                                            mixpanel.track(
                                                HelpAndSupportEvents.ClickedReportBug
                                            );
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        className="top-menu-action-btn img-wrapper"
                                    >
                                        <img
                                            alt="report bug icon"
                                            className="header-icons"
                                            src="https://assets.tenderd.com/header-icons/bug.svg"
                                        />
                                    </div>
                                </Popover>
                            )
                        },
                        {
                            key: 'logout',
                            title: 'Logout',
                            onClick: () => {
                                removeLocalStorage([
                                    'loginResponse',
                                    'geoOverride',
                                    'accountOverride',
                                    'MACHINE_FILTERS',
                                    'tagFilters'
                                ]);
                                window.location.href = '/auth/login';
                            },
                            label: (
                                <div
                                    onClick={() => {
                                        mixpanel.track('User Logged out');
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    className="top-menu-action-btn img-wrapper"
                                >
                                    <img
                                        alt="logout icon"
                                        className="header-icons"
                                        src="https://assets.tenderd.com/header-icons/logout.svg"
                                    />
                                </div>
                            )
                        }
                    ]}
                />
            </div>
        </>
    );
};

export default TopMenus;
